/**
 * WhatsApp Call Listener — processes incoming call webhook events.
 *
 * Listens on the Realtime Database for WhatsApp calling webhook events and
 * dispatches them to the WhatsApp store and module for handling.
 *
 * Call webhook events arrive under the same RTDB path as messages but with
 * a "calls" field in the webhook value instead of "messages".
 *
 * Webhook types (all via field: "calls"):
 *  1. Call Connect — event: "connect", contains SDP answer/offer in session
 *  2. Call Status  — via value.statuses[], status: RINGING | ACCEPTED | REJECTED
 *  3. Call Terminate — event: "terminate", call ended
 */

import { useEffect, useRef } from 'react';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { useModuleStore } from '@/stores/module-store';
import type { WhatsAppModule } from '@/modules/tpa/whatsapp/whatsapp.module';
import type { WhatsAppCallWebhookValue, WhatsAppActiveCall } from '@/modules/tpa/whatsapp/whatsapp-types';

/**
 * Process a raw WhatsApp calling webhook event.
 * Called from the realtime listener when a call-related webhook arrives.
 */
export function processWhatsAppCallWebhook(data: Record<string, unknown>): void {
  const value = extractCallWebhookValue(data);
  if (!value) return;

  const store = useWhatsAppStore.getState();
  const moduleStore = useModuleStore.getState();
  const tpaModule = moduleStore.tpaModule as WhatsAppModule | null;

  if (!tpaModule) return;

  const phoneNumberId = value.metadata?.phone_number_id;

  // ── Handle calls[] entries (connect, terminate, user-initiated incoming) ──
  if (value.calls?.length) {
    for (const call of value.calls) {
      console.log('[WhatsAppCallListener] Call event:', call.event, 'id:', call.id, 'direction:', call.direction);

      switch (call.event) {
        case 'connect': {
          if (call.direction === 'BUSINESS_INITIATED') {
            // SDP answer for our outbound call — complete WebRTC handshake
            if (call.session?.sdp) {
              console.log('[WhatsAppCallListener] Received SDP answer for outbound call');
              tpaModule.handleCallConnect(call.session.sdp);
            }
          } else {
            // USER_INITIATED — incoming call with SDP offer
            const existingCall = store.activeWhatsAppCall;
            if (existingCall && existingCall.status !== 'terminated') {
              console.log('[WhatsAppCallListener] Ignoring incoming call — already have active call');
              break;
            }
            // Username-only callers arrive without `from` — use their BSUID
            const callerId = call.from ?? call.from_user_id ?? '';
            console.log('[WhatsAppCallListener] Incoming call from:', callerId);
            tpaModule.handleIncomingCallWebhook(
              call.id,
              callerId,
              phoneNumberId,
              call.session?.sdp
            );
          }
          break;
        }

        case 'terminate': {
          const activeCall = store.activeWhatsAppCall;
          if (!activeCall || activeCall.callId !== call.id) break;

          console.log('[WhatsAppCallListener] Call terminated:', call.id, 'status:', call.status);
          store.updateCallStatus('terminated');
          store.stopCallDurationTimer();
          setTimeout(() => store.clearActiveCall(), 1000);
          break;
        }
      }
    }
  }

  // ── Handle statuses[] entries (RINGING, ACCEPTED, REJECTED) ──
  if (value.statuses?.length) {
    for (const status of value.statuses) {
      const activeCall = store.activeWhatsAppCall;
      if (!activeCall) continue;

      if (status.id !== activeCall.callId) continue;

      console.log('[WhatsAppCallListener] Call status:', status.status, 'for:', status.id);

      switch (status.status) {
        case 'RINGING':
          store.updateCallStatus('ringing');
          break;
        case 'ACCEPTED':
          store.updateCallStatus('accepted');
          break;
        case 'REJECTED':
          store.updateCallStatus('rejected');
          setTimeout(() => store.clearActiveCall(), 2000);
          break;
      }
    }
  }
}

/** Extract the calling-relevant webhook value from raw data */
function extractCallWebhookValue(data: Record<string, unknown>): WhatsAppCallWebhookValue | null {
  // Direct value object
  if (data.messaging_product === 'whatsapp' && (data.calls || data.statuses)) {
    return data as unknown as WhatsAppCallWebhookValue;
  }

  // Nested under entry[].changes[].value (standard webhook envelope)
  const entry = (data.entry as Array<Record<string, unknown>>)?.[0];
  if (!entry) return null;

  const changes = (entry.changes as Array<Record<string, unknown>>)?.[0];
  if (!changes) return null;

  // Only process calls field webhooks
  if (changes.field !== 'calls') return null;

  const value = changes.value as Record<string, unknown>;
  if (!value) return null;

  if (value.messaging_product === 'whatsapp' && (value.calls || value.statuses)) {
    return value as unknown as WhatsAppCallWebhookValue;
  }

  return null;
}

/**
 * Hook: Monitors the WhatsApp store for active call state changes
 * and ensures cleanup on unmount.
 */
export function useWhatsAppCallCleanup(): void {
  const activeCall = useWhatsAppStore((s) => s.activeWhatsAppCall);
  const clearActiveCall = useWhatsAppStore((s) => s.clearActiveCall);
  const prevCallRef = useRef<WhatsAppActiveCall | null>(null);

  useEffect(() => {
    prevCallRef.current = activeCall;
  }, [activeCall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const call = useWhatsAppStore.getState().activeWhatsAppCall;
      if (call && (call.status === 'terminated' || call.status === 'rejected' || call.status === 'missed')) {
        clearActiveCall();
      }
    };
  }, [clearActiveCall]);
}
